// The seats of a real league, as two screens draw them: the pre-draft page
// (LEAGUE_SETUP.md) and, past the draft, Managers (MANAGERS.md, J4). One row
// per seat, the election when a league has no commissioner, and "You" — hand
// the league over, leave it. Each screen brings its own actions for a seat,
// because what a commissioner may do to one changes at the draft.

import { useState, type ReactNode } from 'react';
import { formatInZone, type ElectionView, type LeagueSetupView, type SeatRef } from '@fsp/domain';
import { useReaderZone } from '../../data/hooks';
import { TeamMark } from '../../kit';
import { Icon } from '../../kit/Icon';
import { Panel } from '../../kit/Panel';
import { AvatarMark, Button, Line, Stack, Text, type WornFace } from '../../kit/ui';
import { Confirm } from './Confirm';
import '../../styles/screens/predraft.css';

/**
 * One seat, in one of the four states a seat can be in, and `actions` beside it.
 *
 * `ownerName === null` is not enough to draw this row, and that was the bug
 * `0008` and `SeatRef.seat` exist to fix: a seat nobody has ever taken is an
 * invitation, and a seat somebody LEFT is a team with a history and nobody in
 * it. Drawing the second as the first quietly erases a person from a league
 * they played in — so it says **Former manager**, which is ACCOUNTS.md rule 6
 * in the one place a reader meets it.
 */
export function SeatRow({
  seat,
  actions,
  openLabel = 'Open seat',
  note = null,
  claimable = true,
}: {
  seat: SeatRef;
  actions: ReactNode;
  /** What a seat nobody has ever taken is called: an invitation before the draft. */
  openLabel?: string;
  /**
   * Whether a friend arriving with the league's code takes a bot's seat — true
   * before the draft only. Past it the code seats nobody, and a bot's team
   * changes hands through the commissioner's link (J4).
   */
  claimable?: boolean;
  /** A line under the seat's own, for the commissioner: this week's lineup trouble (J3). */
  note?: ReactNode;
}) {
  const empty = seat.ownerName === null;
  // A TEAM BROUGHT IN FROM A FILE (H10): its own name, colors and badge,
  // waiting for its manager's link.
  if (empty && seat.named) {
    return (
      <div className="fsp-seat">
        <TeamMark
          team={{ abbreviation: seat.abbreviation, colors: seat.colors, badge: seat.badge }}
          size={32}
        />
        <div className="fsp-grow">
          <Text weight="strong" nowrap block>
            {seat.name}
          </Text>
          <Text size="xs" tone="secondary" nowrap block>
            {seat.invite !== null ? 'Invite link out' : 'Waiting for its manager'}
          </Text>
          {note}
        </div>
        {actions}
      </div>
    );
  }
  if (empty) {
    return (
      <div className="fsp-seat fsp-seat-open">
        {seat.seat === 'bot' && seat.badge?.kind === 'bot' ? (
          // ITS CHARACTER'S FACE (B-464), where a seat a bot holds wears it.
          <TeamMark
            team={{ abbreviation: seat.abbreviation, colors: seat.colors, badge: seat.badge }}
            size={32}
          />
        ) : (
          <span className="fsp-seat-mark fsp-seat-mark-open" aria-hidden>
            <Icon name={seat.seat === 'bot' ? 'bot' : 'invite'} />
          </span>
        )}
        <div className="fsp-grow">
          <Text tone="secondary" block>
            {seat.seat === 'bot' || seat.seat === 'vacated' ? seat.name : openLabel}
          </Text>
          {seat.seat === 'bot' ? (
            <Text size="xs" tone="secondary" nowrap block>
              {seat.botName ?? 'A bot'} is playing it{claimable ? ' · open to a friend' : ''}
            </Text>
          ) : null}
          {seat.seat === 'vacated' ? (
            <Text size="xs" tone="secondary" nowrap block>
              Former manager · the team stands
            </Text>
          ) : null}
          {seat.invite !== null ? (
            <Text size="xs" tone="secondary" nowrap block>
              Invite link out
            </Text>
          ) : null}
          {note}
        </div>
        {actions}
      </div>
    );
  }
  return (
    <div className="fsp-seat">
      <TeamMark
        team={{ abbreviation: seat.abbreviation, colors: seat.colors, badge: seat.badge }}
        size={32}
      />
      <div className="fsp-grow">
        <Text weight="strong" nowrap block>
          {seat.name}
          {seat.isMine ? ' · you' : ''}
        </Text>
        <Text size="xs" tone="secondary" nowrap block>
          {/* THE MANAGER'S OWN FACE beside their name (B-525). */}
          <span className="fsp-seat-person">
            <AvatarMark
              name={seat.ownerName ?? ''}
              color={seat.colors.primary}
              face={seat.ownerFace as WornFace | null}
              size="sm"
            />
          </span>
          {seatPeople(seat)}
          {seat.role === 'commissioner' ? ' · commissioner' : ''}
          {seat.role === 'co-commissioner' ? ' · co-commissioner' : ''}
          {seat.botName !== null ? ` · ${seat.botName} is playing this week` : ''}
        </Text>
        {note}
      </div>
      {actions}
    </div>
  );
}

/**
 * The vote, when a league has nobody running it.
 *
 * COUNTS, NEVER A VOTER LIST. Each candidate shows how many votes they have
 * and the caller sees their own choice; there is no shape here, and none in
 * the payload behind it, that says who voted for whom.
 */
export function Election({
  election,
  busy,
  onVote,
  onResolve,
}: {
  election: ElectionView;
  busy: boolean;
  onVote: (teamId: string) => void;
  onResolve: () => void;
}) {
  const zone = useReaderZone();
  const closes =
    zone === null
      ? 'soon'
      : formatInZone(election.closesAt, zone, {
          weekday: 'long',
          hour: 'numeric',
          minute: '2-digit',
        });
  if (election.decided) {
    return (
      <Text block>
        {election.decided.displayName} runs this league now
        {election.decided.by === 'tenure' ? ', as the longest-serving manager' : ''}.
      </Text>
    );
  }
  return (
    <Stack gap={2}>
      <Text tone="secondary" block>
        {election.votesCast} of {election.seatedManagers} managers have voted. {election.majorityAt}{' '}
        decides it, or it is counted {closes}.
      </Text>
      <div className="fsp-seats">
        {election.candidates.map((candidate) => (
          <div className="fsp-seat" key={candidate.teamId}>
            <div className="fsp-grow">
              <Text weight="strong" nowrap block>
                {candidate.displayName}
                {candidate.isMe ? ' · you' : ''}
              </Text>
              <Text size="xs" tone="secondary" block>
                {candidate.votes} {candidate.votes === 1 ? 'vote' : 'votes'}
                {election.myVote === candidate.teamId ? ' · your vote' : ''}
              </Text>
            </div>
            {election.canVote && election.open ? (
              <div className="fsp-predraft-seat-actions">
                <Button
                  size="xs"
                  variant={election.myVote === candidate.teamId ? 'soft' : 'secondary'}
                  disabled={busy}
                  onClick={() => onVote(candidate.teamId)}
                >
                  {election.myVote === candidate.teamId ? 'Voted' : 'Vote'}
                </Button>
              </div>
            ) : null}
          </div>
        ))}
      </div>
      {!election.open ? (
        // The deadline has passed and nobody reached a majority. Nothing counts
        // the votes on a schedule yet, so the count happens when somebody asks
        // for it — and the rule it applies is already decided: the most votes,
        // and the longest tenure breaks a tie.
        <Line>
          <Button disabled={busy} onClick={onResolve}>
            Count the votes
          </Button>
        </Line>
      ) : null}
    </Stack>
  );
}

/** A league with nobody running it: the vote, or the button that starts one. */
export function NoCommissioner({
  view,
  busy,
  act,
}: {
  view: LeagueSetupView;
  busy: boolean;
  act: (path: string, body: unknown, done: string) => void;
}) {
  return (
    <Panel title="This league has no commissioner">
      <Stack gap={2}>
        <Text tone="secondary" block>
          Nobody can change the rules, the draft time or the seats until the managers pick somebody.
          Every manager gets one vote.
        </Text>
        {view.election ? (
          <Election
            election={view.election}
            busy={busy}
            onVote={(teamId) => act('/election', { intent: 'vote', teamId }, 'Vote counted.')}
            onResolve={() => act('/election', { intent: 'resolve' }, 'Votes counted.')}
          />
        ) : (
          <Line>
            <Button
              disabled={busy}
              onClick={() => act('/election', { intent: 'open' }, 'The vote is open.')}
            >
              Start the vote
            </Button>
          </Line>
        )}
      </Stack>
    </Panel>
  );
}

/**
 * Who runs a seat, as its row says it: *Sam*, *Sam & Jamie*, *Sam, Jamie &
 * Riley*, the manager first (B-437).
 */
export function seatPeople(seat: SeatRef): string {
  const all = [seat.ownerName ?? '', ...seat.coManagers].filter((n) => n.length > 0);
  if (all.length <= 1) return all[0] ?? '';
  return `${all.slice(0, -1).join(', ')} & ${all[all.length - 1]}`;
}

/**
 * Hand the league over, and leave it.
 *
 * WHAT LEAVING DOES IS SAID BEFORE IT IS DONE, and it is more past the draft:
 * the team stands with nobody, its claims are withdrawn and its unanswered
 * offers void (MANAGERS.md, J4).
 */
export function YouPanel({
  view,
  busy,
  act,
  drafted,
}: {
  view: LeagueSetupView;
  busy: boolean;
  act: (path: string, body: unknown, done: string) => void;
  drafted: boolean;
}) {
  const [handOverTo, setHandOverTo] = useState<string | null>(null);
  const mySeat = view.seats.find((s) => s.isMine) ?? null;
  const others = view.seats.filter((s) => s.seat === 'manager' && !s.isMine);
  // A SHARED TEAM (B13): a co-manager leaving takes nothing with them, and a
  // manager leaving hands the team to the longest-standing co-manager.
  const coManaging = mySeat?.isCoManager === true;
  const heir = !coManaging ? (mySeat?.coManagers[0] ?? null) : null;
  return (
    <Panel title="You">
      <Stack gap={4}>
        {view.canManage && others.length > 0 ? (
          <Stack gap={2}>
            <div>
              <Text weight="strong" block>
                Hand the league to somebody else
              </Text>
              <Text size="xs" tone="secondary" block>
                They run it from then on; you keep your team and stay in the league.
              </Text>
            </div>
            <Line wrap>
              {others.map((seat) => (
                <Button
                  key={seat.teamId}
                  size="xs"
                  variant={handOverTo === seat.teamId ? 'primary' : 'secondary'}
                  disabled={busy}
                  onClick={() => setHandOverTo(handOverTo === seat.teamId ? null : seat.teamId)}
                >
                  {seat.ownerName}
                </Button>
              ))}
            </Line>
            {handOverTo !== null ? (
              <Line wrap>
                <Button
                  size="xs"
                  variant="primary"
                  disabled={busy}
                  onClick={() => {
                    act(
                      '/commissioner',
                      { teamId: handOverTo, keepAs: 'co-commissioner' },
                      'Handed over. You are a co-commissioner.',
                    );
                    setHandOverTo(null);
                  }}
                >
                  Hand over, stay a co-commissioner
                </Button>
                <Button
                  size="xs"
                  disabled={busy}
                  onClick={() => {
                    act(
                      '/commissioner',
                      { teamId: handOverTo, keepAs: 'manager' },
                      'Handed over. You are a manager here now.',
                    );
                    setHandOverTo(null);
                  }}
                >
                  Hand over, step all the way back
                </Button>
              </Line>
            ) : null}
          </Stack>
        ) : null}
        <Stack gap={2}>
          <div>
            <Text weight="strong" block>
              Leave this league
            </Text>
            <Text size="xs" tone="secondary" block>
              {mySeat === null
                ? 'You are watching this league rather than playing in it.'
                : coManaging
                  ? `You stop helping run ${mySeat.name} and leave the league. The team, its claims and its offers stay with ${mySeat.ownerName ?? 'its manager'}.`
                  : heir !== null
                    ? `${heir} takes over ${mySeat.name}, with its roster, its record, its claims and its offers.`
                    : drafted
                      ? 'Your team stays, with its name, roster and record, and the commissioner can invite somebody to take it over. Your waiver claims are withdrawn, and trade offers to or from your team that nobody has accepted are void.'
                      : 'Your seat opens for somebody else. The team stays, with its name and its record.'}
            </Text>
          </div>
          <Confirm
            label="Leave the league"
            confirmLabel="Leave it?"
            busy={busy}
            onConfirm={() => act('/leave', undefined, 'You have left the league.')}
          />
        </Stack>
        {drafted && mySeat !== null && !coManaging && heir === null ? (
          // THE BOT AS HELP, NOT AS REPLACEMENT (AI_MANAGERS.md §8.3): a way to
          // stop without leaving the league a team nobody plays (J3).
          <Stack gap={2}>
            <div>
              <Text weight="strong" block>
                Let a bot play {mySeat.name}
              </Text>
              <Text size="xs" tone="secondary" block>
                You leave the league, and a bot plays the team from then on, so the league keeps
                moving: it sets the lineup before each kickoff, picks up players and offers trades
                in its own style, and answers trade offers and votes on trades. Your waiver claims
                are withdrawn and the offers nobody has accepted are void, as when you leave. If you
                come back, the commissioner can send you the team’s link.
              </Text>
            </div>
            <Confirm
              label="Hand it to a bot"
              confirmLabel="Leave, and hand it over?"
              busy={busy}
              onConfirm={() =>
                act(
                  '/leave',
                  { team: 'bot' },
                  `You have left the league. A bot plays ${mySeat.name}.`,
                )
              }
            />
          </Stack>
        ) : null}
      </Stack>
    </Panel>
  );
}
